import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronRight, TrendingUp, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { ThemeToggle } from "@/components/ThemeProvider";
import { ExpressProgressBar } from "@/components/partner/ExpressProgressBar";
import { PropertyBasicsScreen, isPropertyBasicsComplete } from "@/components/partner/PropertyBasicsScreen";
import { LayoutFeaturesScreen, isLayoutFeaturesComplete } from "@/components/partner/LayoutFeaturesScreen";
import { InvestorContextScreen, isInvestorContextComplete } from "@/components/investor/InvestorContextScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCREEN_TITLES = [
  { title: "Property Details", subtitle: "Tell us about the property" },
  { title: "Layout & Features", subtitle: "What's the property like inside?" },
  { title: "Investment Context", subtitle: "Your investment strategy" },
];

// Smart defaults for investors
const SMART_DEFAULTS = {
  property_era: "classic",
  dwelling_type: "semi-detached",
  condition: "fair",
  investment_strategy: "buy_to_let",
};

const INITIAL_STATE = {
  // Screen 1 - Property Basics
  property_era: SMART_DEFAULTS.property_era,
  dwelling_type: SMART_DEFAULTS.dwelling_type,
  bedrooms: "",
  bathrooms: "",
  epc_band: "",
  // Screen 2 - Layout & Features
  separate_kitchen: "",
  wide_hallways: "no",
  layout_changed: "no",
  features: [],
  // Screen 3 - Investor Context
  investment_strategy: SMART_DEFAULTS.investment_strategy,
  condition: SMART_DEFAULTS.condition,
  purchase_price: "",
  renovation_budget: "",
  target_rent: "",
  holding_period: "5",
  notes: "",
};

export default function InvestorExpress() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [currentScreen, setCurrentScreen] = useState(1);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [sessionInfo, setSessionInfo] = useState({ project_name: "", postcode: "" });

  useEffect(() => {
    const storedSession = sessionStorage.getItem(`investor_session_${sessionId}`);
    if (storedSession) {
      const parsed = JSON.parse(storedSession);
      setSessionInfo({
        project_name: parsed.project_name || "Project",
        postcode: parsed.postcode || "",
      });
    }
  }, [sessionId]);

  const canProceed = () => {
    switch (currentScreen) {
      case 1:
        return isPropertyBasicsComplete(data);
      case 2:
        return isLayoutFeaturesComplete(data);
      case 3:
        return isInvestorContextComplete(data);
      default:
        return false;
    }
  };

  const goNext = () => {
    if (currentScreen < 3) {
      setDirection(1);
      setCurrentScreen(currentScreen + 1);
    } else {
      handleSubmit();
    }
  };

  const goBack = () => {
    if (currentScreen > 1) {
      setDirection(-1);
      setCurrentScreen(currentScreen - 1);
    } else {
      navigate("/investor");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const investorContext = {
        investment_strategy: data.investment_strategy,
        condition: data.condition,
        purchase_price: data.purchase_price ? parseInt(data.purchase_price.replace(/,/g, '')) : null,
        renovation_budget: data.renovation_budget ? parseInt(data.renovation_budget.replace(/,/g, '')) : null,
        target_rent: data.target_rent ? parseInt(data.target_rent.replace(/,/g, '')) : null,
        holding_period: parseInt(data.holding_period) || 5,
        notes: data.notes?.trim() || null,
      };
      
      const features = (data.features || []).filter(f => f !== "none");
      
      const payload = {
        first_name: sessionInfo.project_name || "Investment",
        postcode: sessionInfo.postcode,
        property_era: data.property_era,
        dwelling_type: data.dwelling_type,
        bedrooms: data.bedrooms,
        bathrooms: parseInt(data.bathrooms) || 1,
        separate_kitchen: data.separate_kitchen,
        wide_hallways_over_1m: data.wide_hallways,
        layout_changed_since_built: data.layout_changed,
        features: features,
        epc_band: data.epc_band,
        main_goal: data.investment_strategy === "flip" ? "sell" : "rent",
        email: "investor@cipher.app",
        consent: true,
        is_investor: true,
        investor_context: investorContext,
      };

      const response = await axios.post(`${API}/hippo/teaser`, payload);
      const submissionId = response.headers["x-submission-id"];
      const accessToken = response.headers["x-access-token"];
      
      if (!submissionId) throw new Error("No submission ID returned");
      
      sessionStorage.setItem(`hippo_data_${submissionId}`, JSON.stringify(response.data));
      sessionStorage.setItem(`hippo_token_${submissionId}`, accessToken);
      sessionStorage.setItem(`hippo_session_${submissionId}`, JSON.stringify({
        first_name: sessionInfo.project_name,
        postcode: sessionInfo.postcode,
        dwelling_type: data.dwelling_type,
        bedrooms: data.bedrooms,
        features: features,
        separate_kitchen: data.separate_kitchen,
        main_goal: data.investment_strategy === "flip" ? "sell" : "rent",
      }));
      sessionStorage.setItem(`investor_context_${submissionId}`, JSON.stringify(investorContext));
      
      navigate(`/hippo/${submissionId}`);
    } catch (error) {
      console.error("Investor submission error:", error);
      toast.error("Failed to generate analysis. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -300 : 300, opacity: 0 }),
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 1:
        return <PropertyBasicsScreen data={data} onChange={setData} />;
      case 2:
        return <LayoutFeaturesScreen data={data} onChange={setData} />;
      case 3:
        return <InvestorContextScreen data={data} onChange={setData} />;
      default:
        return null;
    }
  };

  const screenTitle = SCREEN_TITLES[currentScreen - 1];

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goBack}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white"
              data-testid="investor-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <span className="text-sm font-bold text-white">Investor Analysis</span>
            </div>
            
            <ThemeToggle />
          </div>
          
          <ExpressProgressBar currentStep={currentScreen} totalSteps={3} />
        </div>
      </header>

      {/* Project Info Bar */}
      <div className="flex-shrink-0 px-4 py-2 bg-white/5 border-b border-white/5">
        <div className="max-w-lg mx-auto flex items-center justify-center gap-3 text-sm">
          <span className="text-white/70">{sessionInfo.project_name}</span>
          <span className="text-white/30">•</span>
          <span className="flex items-center gap-1 text-blue-400">
            <MapPin className="w-3 h-3" />
            {sessionInfo.postcode}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full max-w-lg mx-auto p-4">
          <div className="mb-6">
            <h1 className="text-xl font-bold text-white">{screenTitle.title}</h1>
            <p className="text-sm text-white/50 mt-1">{screenTitle.subtitle}</p>
          </div>

          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentScreen}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                className="overflow-y-auto max-h-[calc(100vh-320px)] pb-4 pr-2"
                style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.2) transparent" }}
              >
                {renderScreen()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="flex-shrink-0 p-4 border-t border-white/10 bg-slate-900/80">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={goNext}
            disabled={!canProceed() || submitting}
            className={`w-full py-4 text-base font-medium rounded-xl transition-all ${
              canProceed()
                ? "bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                : "bg-white/10 text-white/40 cursor-not-allowed"
            }`}
            data-testid="investor-continue-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Analysing...
              </span>
            ) : currentScreen === 3 ? (
              <span className="flex items-center gap-2">
                Generate ROI Analysis
                <ChevronRight className="w-4 h-4" />
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Continue
                <ChevronRight className="w-4 h-4" />
              </span>
            )}
          </Button>
          
          {currentScreen === 3 && (
            <p className="text-[10px] text-white/30 text-center mt-3">
              Investor analyses include yield calculations and ROI projections.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
